/**
 * Streak Tracking Service
 *
 * Manages daily streak tracking for gamification.
 * Tracks consecutive days of learning activity.
 */

import { ref as dbRef, get, set } from 'firebase/database';
import { db } from '@/lib/firebase';
import { StreakData } from '@/types/models';

/**
 * Streak milestones for future celebration UI
 */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 90];

/**
 * Convert timestamp to YYYY-MM-DD format
 */
function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayString(): string {
  return formatDate(Date.now());
}

/**
 * Calculate days between two YYYY-MM-DD dates
 */
function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get streak data for a user
 */
export async function getStreak(userId: string): Promise<StreakData | null> {
  const streakRef = dbRef(db, `streaks/${userId}`);
  const snapshot = await get(streakRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.val() as StreakData;
}

/**
 * Update streak when user completes a learning activity
 *
 * Logic:
 * - If lastActiveDate is today: no change, return current
 * - If lastActiveDate is yesterday: increment currentStreak
 * - If lastActiveDate is older: reset currentStreak to 1
 * - Update longestStreak if currentStreak exceeds it
 * - Save lastActiveDate as today
 */
export async function updateStreak(userId: string): Promise<StreakData> {
  const today = getTodayString();
  const streakRef = dbRef(db, `streaks/${userId}`);
  const snapshot = await get(streakRef);

  let streakData: StreakData;

  if (!snapshot.exists()) {
    // Initialize new streak - use lastActivityDate to match database rules
    streakData = {
      lastActivityDate: Date.now(),
      currentStreak: 1,
      longestStreak: 1,
      totalActiveDays: 1,
    };
  } else {
    const currentData = snapshot.val() as StreakData;
    // Support both old field name (lastActiveDate) and new (lastActivityDate)
    const lastDate = currentData.lastActivityDate
      ? formatDate(currentData.lastActivityDate)
      : (currentData as any).lastActiveDate || today;

    // If activity already recorded today, no change
    if (lastDate === today) {
      return currentData;
    }

    const daysSinceLastActivity = daysBetween(lastDate, today);

    if (daysSinceLastActivity === 1) {
      // Consecutive day - increment streak
      const newCurrentStreak = currentData.currentStreak + 1;
      streakData = {
        lastActivityDate: Date.now(),
        currentStreak: newCurrentStreak,
        longestStreak: Math.max(newCurrentStreak, currentData.longestStreak),
        totalActiveDays: (currentData.totalActiveDays || currentData.currentStreak || 0) + 1,
      };
    } else {
      // Streak broken - reset to 1
      streakData = {
        lastActivityDate: Date.now(),
        currentStreak: 1,
        longestStreak: currentData.longestStreak,
        totalActiveDays: (currentData.totalActiveDays || currentData.currentStreak || 0) + 1,
      };
    }
  }

  // Save to database
  await set(streakRef, streakData);

  return streakData;
}

/**
 * Check if streak is at a milestone
 */
export function isStreakMilestone(streak: number): boolean {
  return STREAK_MILESTONES.includes(streak);
}

/**
 * Get next milestone for a given streak
 */
export function getNextMilestone(currentStreak: number): number | null {
  const next = STREAK_MILESTONES.find(milestone => milestone > currentStreak);
  return next || null;
}

/**
 * Check if user's streak is at risk (last activity was yesterday)
 */
export async function isStreakAtRisk(userId: string): Promise<boolean> {
  const streakData = await getStreak(userId);
  if (!streakData) {
    return false;
  }

  const today = getTodayString();
  const lastDateStr = formatDate(streakData.lastActivityDate);
  const daysSinceLastActivity = daysBetween(lastDateStr, today);

  // Streak is at risk if last activity was yesterday (1 day ago)
  return daysSinceLastActivity === 1 && streakData.currentStreak > 0;
}
